import { CSV_IMPORT_MAX_ROWS } from '../../shared/csv-core/domain/csvConstants';
import { buildCsvFromRows } from '../../shared/csv-core/infra/buildCsv';
import { parseCsvText, rowToRecord } from '../../shared/csv-core/infra/parseCsv';
import type { CsvCommitResult, CsvPreviewResult, CsvPreviewRow } from '../../shared/csv/hooks/csvFlowAdapter';
import type { CustomerAccountRow } from '../domain/customerTypes';
import { countCustomerAccounts, createCustomer, fetchCustomerAccounts, updateCustomerProfile } from './customerAccounts';
import { CUSTOMER_CSV_HEADERS, CUSTOMER_CSV_REQUIRED_HEADERS } from './customerCsvSchemas';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type CustomerCsvRecord = Record<string, string>;

type CustomerCsvProfile = {
  name: string;
  phone?: string;
  email?: string;
  cuit?: string;
  address?: string;
  taxCategory?: string;
  notes?: string;
  paymentTermsDays?: number;
};

function normalizePhone(value: string | null | undefined): string {
  return (value ?? '').replace(/\D/g, '');
}

function normalizeEmail(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

function value(record: CustomerCsvRecord, column: string): string {
  return record[column]?.trim() ?? '';
}

function parsePaymentTerms(valueText: string): number | undefined {
  if (!valueText) return undefined;
  if (!/^\d+$/.test(valueText)) throw new Error('dias_pago debe ser un número entero mayor o igual a 0.');
  return Number(valueText);
}

function parseProfile(record: CustomerCsvRecord): CustomerCsvProfile {
  const name = value(record, 'nombre');
  const phone = value(record, 'telefono');
  const email = normalizeEmail(value(record, 'email'));
  if (!name) throw new Error('El nombre es obligatorio.');
  if (!normalizePhone(phone) && !email) throw new Error('Completá teléfono o email.');
  if (email && !EMAIL_PATTERN.test(email)) throw new Error('El email no es válido.');
  if (phone && normalizePhone(phone).length < 6) throw new Error('El teléfono no es válido.');

  const paymentTermsDays = parsePaymentTerms(value(record, 'dias_pago'));
  return {
    name,
    ...(phone ? { phone } : {}),
    ...(email ? { email } : {}),
    ...(value(record, 'cuit') ? { cuit: value(record, 'cuit') } : {}),
    ...(value(record, 'direccion') ? { address: value(record, 'direccion') } : {}),
    ...(value(record, 'categoria_fiscal') ? { taxCategory: value(record, 'categoria_fiscal') } : {}),
    ...(value(record, 'notas') ? { notes: value(record, 'notas') } : {}),
    ...(paymentTermsDays !== undefined ? { paymentTermsDays } : {}),
  };
}

function findMatchingCustomer(rows: CustomerAccountRow[], profile: CustomerCsvProfile): CustomerAccountRow | null {
  const phone = profile.phone ? normalizePhone(profile.phone) : '';
  const email = profile.email ? normalizeEmail(profile.email) : '';
  const phoneMatch = phone
    ? rows.find((row) => normalizePhone(row.phone) === phone)
    : null;
  const emailMatch = email
    ? rows.find((row) => normalizeEmail(row.email) === email)
    : null;
  if (phoneMatch && emailMatch && phoneMatch.id !== emailMatch.id) {
    throw new Error('El teléfono y el email pertenecen a clientes distintos.');
  }
  return phoneMatch ?? emailMatch ?? null;
}

function profileChanges(
  customer: CustomerAccountRow,
  profile: CustomerCsvProfile,
): Parameters<typeof updateCustomerProfile>[1] {
  const changes: Parameters<typeof updateCustomerProfile>[1] = {};
  if (profile.name !== customer.name) changes.name = profile.name;
  if (profile.phone !== undefined && normalizePhone(profile.phone) !== normalizePhone(customer.phone)) changes.phone = profile.phone;
  if (profile.email !== undefined && normalizeEmail(profile.email) !== normalizeEmail(customer.email)) changes.email = profile.email;
  if (profile.cuit !== undefined && profile.cuit !== (customer.cuit ?? '')) changes.cuit = profile.cuit;
  if (profile.address !== undefined && profile.address !== (customer.address ?? '')) changes.address = profile.address;
  if (profile.taxCategory !== undefined && profile.taxCategory !== (customer.taxCategory ?? '')) changes.taxCategory = profile.taxCategory;
  if (profile.notes !== undefined && profile.notes !== (customer.notes ?? '')) changes.notes = profile.notes;
  if (profile.paymentTermsDays !== undefined && profile.paymentTermsDays !== (customer.paymentTermsDays ?? 0)) {
    changes.paymentTermsDays = profile.paymentTermsDays;
  }
  return changes;
}

async function loadCustomerImportRows(): Promise<CustomerAccountRow[]> {
  const total = await countCustomerAccounts();
  if (total === 0) return [];
  return fetchCustomerAccounts({ limit: total, offset: 0 });
}

function blockedResult(message: string, totalRows = 0): CsvPreviewResult {
  return {
    totalRows,
    okCount: 0,
    skipCount: 0,
    errorCount: 1,
    rows: [{ rowNumber: 1, status: 'error', action: 'validar', detail: 'archivo', message }],
    blocked: true,
    blockMessage: message,
  };
}

export async function previewCustomerCsvImport(csvText: string): Promise<CsvPreviewResult> {
  const parsed = parseCsvText(csvText);
  const missingHeaders = CUSTOMER_CSV_REQUIRED_HEADERS.filter((header) => !parsed.headers.includes(header));
  if (missingHeaders.length > 0) return blockedResult(`Falta la columna obligatoria: ${missingHeaders.join(', ')}.`);
  if (!parsed.headers.includes('telefono') && !parsed.headers.includes('email')) {
    return blockedResult('El archivo debe incluir la columna telefono o email.');
  }
  if (parsed.rows.length > CSV_IMPORT_MAX_ROWS) {
    return blockedResult(`El archivo tiene más de ${CSV_IMPORT_MAX_ROWS} filas. Dividilo en partes más chicas.`, parsed.rows.length);
  }

  const existingRows = await loadCustomerImportRows();
  const seenPhones = new Set<string>();
  const seenEmails = new Set<string>();
  const rows: CsvPreviewRow[] = [];
  let okCount = 0;
  let skipCount = 0;
  let errorCount = 0;

  for (let index = 0; index < parsed.rows.length; index += 1) {
    const rowNumber = index + 2;
    try {
      const record = rowToRecord(parsed.headers, parsed.rows[index]);
      const profile = parseProfile(record);
      const phone = profile.phone ? normalizePhone(profile.phone) : '';
      const email = profile.email ? normalizeEmail(profile.email) : '';
      if ((phone && seenPhones.has(phone)) || (email && seenEmails.has(email))) {
        throw new Error('La fila repite un teléfono o email del archivo.');
      }
      if (phone) seenPhones.add(phone);
      if (email) seenEmails.add(email);

      const matchingCustomer = findMatchingCustomer(existingRows, profile);
      if (!matchingCustomer) {
        rows.push({ rowNumber, status: 'ok', action: 'crear', detail: profile.name });
        okCount += 1;
        continue;
      }
      const changes = profileChanges(matchingCustomer, profile);
      if (Object.keys(changes).length === 0) {
        rows.push({ rowNumber, status: 'skip', action: 'omitir', detail: `${profile.name} · sin cambios` });
        skipCount += 1;
      } else {
        rows.push({ rowNumber, status: 'ok', action: 'actualizar', detail: `${profile.name} · datos de contacto` });
        okCount += 1;
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'No se pudo validar la fila.';
      rows.push({ rowNumber, status: 'error', action: 'validar', detail: 'fila', message });
      errorCount += 1;
    }
  }

  return { totalRows: parsed.rows.length, okCount, skipCount, errorCount, rows };
}

export async function commitCustomerCsvImport(csvText: string): Promise<CsvCommitResult> {
  const preview = await previewCustomerCsvImport(csvText);
  if (preview.blocked) throw new Error(preview.blockMessage ?? 'Importación bloqueada.');

  const parsed = parseCsvText(csvText);
  const existingRows = await loadCustomerImportRows();
  const errors: Array<{ rowNumber: number; message: string }> = [];
  let applied = 0;
  let skipped = 0;
  let failed = 0;
  let altas = 0;
  let ediciones = 0;

  for (let index = 0; index < parsed.rows.length; index += 1) {
    const rowNumber = index + 2;
    const previewRow = preview.rows[index];
    if (!previewRow || previewRow.status === 'error') {
      failed += 1;
      if (previewRow?.message) errors.push({ rowNumber, message: previewRow.message });
      continue;
    }
    if (previewRow.status === 'skip') {
      skipped += 1;
      continue;
    }

    try {
      const profile = parseProfile(rowToRecord(parsed.headers, parsed.rows[index]));
      const matchingCustomer = findMatchingCustomer(existingRows, profile);
      if (!matchingCustomer) {
        await createCustomer({
          name: profile.name,
          phone: profile.phone ?? '',
          cuit: profile.cuit ?? null,
          creditLimit: 0,
          email: profile.email ?? null,
          address: profile.address ?? null,
          taxCategory: profile.taxCategory ?? null,
          notes: profile.notes ?? null,
          paymentTermsDays: profile.paymentTermsDays ?? 0,
        });
        altas += 1;
      } else {
        const changes = profileChanges(matchingCustomer, profile);
        await updateCustomerProfile(matchingCustomer.id, changes);
        ediciones += 1;
      }
      applied += 1;
    } catch (error) {
      failed += 1;
      errors.push({ rowNumber, message: error instanceof Error ? error.message : 'No se pudo aplicar la fila.' });
    }
  }

  return { applied, skipped, failed, errors, altas, ediciones };
}

export function buildCustomerImportErrorsCsv(errors: Array<{ rowNumber: number; message: string }>): string {
  return buildCsvFromRows(['fila', 'mensaje'], errors.map((error) => [String(error.rowNumber), error.message]));
}

export function buildCustomerCsvTemplate(): string {
  return buildCsvFromRows([...CUSTOMER_CSV_HEADERS], [
    ['Ana Pérez', '5491112345678', 'ana@example.com', '20-12345678-9', 'Av. Siempre Viva 123', 'consumidor_final', 'Cliente frecuente', '30'],
  ]);
}

export function buildCustomerCsvExport(rows: CustomerAccountRow[]): string {
  return buildCsvFromRows(
    [...CUSTOMER_CSV_HEADERS],
    rows.map((customer) => [
      customer.name,
      customer.phone ?? '',
      customer.email ?? '',
      customer.cuit ?? '',
      customer.address ?? '',
      customer.taxCategory ?? '',
      customer.notes ?? '',
      customer.paymentTermsDays != null ? String(customer.paymentTermsDays) : '',
    ]),
  );
}
